import React, { useState, useEffect } from 'react';
import {
  Rocket,
  Video,
  Sparkles,
  FileText,
  MessageCircle,
  Youtube,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActionDialog, {
  ActionType,
} from '../features/globalModals/ActionDialog';
import { pushModal } from '../features/globalModals/globalModalsSlice';
import { setSourceFeature, selectPlan } from '../features/plan/planSlice';
import { initializeRecorder } from '../features/recording/recordingSlice';

interface SocialMediaImportDialogProps {
  open: boolean;
  onClose: () => void;
  url: string;
}

const SocialMediaImportDialog: React.FC<SocialMediaImportDialogProps> = ({
  open,
  onClose,
  url,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<{
    teamId: string;
    folderId: string;
  }>();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [contentTitle, setContentTitle] = useState<string | null>(null);
  const [contentType, setContentType] = useState<'youtube' | null>(null);

  // Get media content info (YouTube only)
  useEffect(() => {
    const getMediaInfo = async () => {
      if (!url) return;

      // YouTube patterns
      const youtubePatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
        /youtube\.com\/embed\/([^&\n?#]+)/,
      ];

      // Check for YouTube
      const youtubeMatch = youtubePatterns
        .map((pattern) => url.match(pattern))
        .find((match) => match && match[1]);

      if (youtubeMatch) {
        const videoId = youtubeMatch[1];
        setContentType('youtube');
        // Set higher quality thumbnail
        setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`);

        // Get title
        try {
          const response = await fetch(
            `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`,
          );
          const data = await response.json();
          setContentTitle(data.title || null);
        } catch (error) {
          console.error('Error fetching YouTube title:', error);
        }
      }
    };

    getMediaInfo();
  }, [url]);

  const handleUpgrade = () => {
    // Set the source feature to youtubeInstagramImports
    dispatch(
      setSourceFeature({
        sourceFeature: 'youtubeInstagramImports',
      }),
    );

    // Select the GROWTH plan
    dispatch(
      selectPlan({
        plan: 'GROWTH',
      }),
    );

    // Push the upgrade modal
    dispatch(
      pushModal({
        type: 'stripe-pricing-table-upgrade-modal',
        source: 'limits',
      }),
    );

    // Close this dialog
    onClose();
  };

  const handleRecord = () => {
    // Initialize screen recorder
    if (teamId && folderId) {
      const settings = {
        audioSettings: { microphoneAudio: true, browserAudio: true },
        videoSettings: { screen: true, webCam: true },
      };
      dispatch(initializeRecorder({ teamId, folderId, settings }));
      onClose();
    }
  };

  const actions: ActionType[] = [
    {
      key: 'record',
      label: t('import.social.actions.recordInstead'),
      onClick: handleRecord,
      variant: 'outlined',
      color: 'secondary',
      startIcon: <Video className="h-4 w-4" />,
      'data-testid': 'import-youtube-record-btn',
    },
    {
      key: 'upgrade',
      label: t('import.social.actions.upgradeNow'),
      onClick: handleUpgrade,
      variant: 'contained',
      color: 'primary',
      startIcon: <Rocket className="h-4 w-4" />,
      'data-testid': 'import-youtube-upgrade-btn',
      sx: { fontWeight: 600, px: 3 },
    },
  ];

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={t('import.social.title')}
      actions={actions}
      maxWidth="sm"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center p-4 rounded-lg bg-muted/50 relative">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={
                contentType === 'youtube'
                  ? 'YouTube thumbnail'
                  : 'Instagram thumbnail'
              }
              className="w-30 h-17 object-cover rounded-md mr-4 flex-shrink-0"
            />
          ) : (
            <div className="flex items-center justify-center w-30 h-17 bg-black/5 rounded-md mr-4 flex-shrink-0">
              {contentType === 'youtube' ? (
                <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center">
                  <Youtube className="h-8 w-8 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gray-700 flex items-center justify-center">
                  <Video className="h-8 w-8 text-white" />
                </div>
              )}
            </div>
          )}
          <div className="overflow-hidden">
            <p className="font-medium text-foreground mb-1">
              {contentTitle || t('import.social.youtubeVideo')}
            </p>
            <p className="text-sm text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
              {url}
            </p>
          </div>

          {/* Ready indicator with minimal text */}
          <div className="absolute bottom-2 right-2 flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            <span className="text-[0.65rem] text-green-500 leading-none">
              {t('import.social.readyToImport')}
            </span>
          </div>
        </div>

        <p className="text-center text-foreground font-medium text-lg mb-3">
          {t('import.social.upgradeMessage')}
        </p>

        <div className="p-5 bg-muted/30 rounded-lg border-0">
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded bg-primary flex items-center text-white">
                <Youtube className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="font-medium text-sm mb-1">
                  {t('import.social.features.youtube.title')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('import.social.features.youtube.description')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="p-2 rounded bg-primary flex items-center text-white">
                <MessageCircle className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="font-medium text-sm mb-1">
                  {t('import.social.features.transcription.title')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('import.social.features.transcription.description')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="p-2 rounded bg-primary flex items-center text-white">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="font-medium text-sm mb-1">
                  {t('import.social.features.ai.title')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('import.social.features.ai.description')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="p-2 rounded bg-primary flex items-center text-white">
                <FileText className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="font-medium text-sm mb-1">
                  {t('import.social.features.documents.title')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('import.social.features.documents.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ActionDialog>
  );
};

export default SocialMediaImportDialog;
